/**
 * Admin Maintenance Task Detail Page
 *
 * Shows full details for a single task definition.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertTriangle, Check, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Navbar } from '../../components/Navbar';
import Footer from '../../components/Footer';
import api from '../../services/authenticatedApi';
import { logger } from '../../utils/logger';

interface TaskDef {
  taskClass: string;
  displayName: string;
  description: string | null;
  icon: string | null;
  priority: string;
  category: string | null;
  keywords: string[];
  applicability: Record<string, unknown> | null;
}

const EXCLUDE_LABELS: Record<string, string> = {
  hasTools: 'Has tools',
  hasPractices: 'Has practice',
};

function RuleRow({
  label,
  items,
  variant,
}: {
  label: string;
  items: string[];
  variant: 'require' | 'exclude';
}) {
  const tagCls =
    variant === 'require'
      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40'
      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/40';
  const labelCls =
    variant === 'require'
      ? 'text-emerald-600/60 dark:text-emerald-500/60'
      : 'text-red-500/60 dark:text-red-400/60';

  return (
    <div className="flex items-center gap-3">
      <span
        className={`text-[11px] uppercase tracking-wider font-medium shrink-0 whitespace-nowrap ${labelCls}`}
      >
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span
            key={item}
            className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${tagCls}`}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function ApplicabilityGrid({ rules }: { rules: Record<string, unknown> }) {
  const platform = rules.platform as string | undefined;
  const langs = rules.requiredLanguages as string[] | undefined;
  const tools = rules.requiredTools as string[] | undefined;
  const practices = rules.requiredPractices as string[] | undefined;
  const exclude = rules.excludeWhen as Record<string, string[]> | undefined;

  const hasRequires = platform || langs || tools || practices;
  const hasExcludes = exclude && Object.keys(exclude).length > 0;

  if (!hasRequires && !hasExcludes) {
    return (
      <p className="text-sm text-gray-400">No applicability rules — runs on any repository.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Requires column */}
      <div className="rounded-lg bg-emerald-50/50 dark:bg-emerald-900/5 border border-emerald-100 dark:border-emerald-900/20 p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5" /> Required When
        </h3>
        {hasRequires ? (
          <div className="space-y-2">
            {platform && <RuleRow variant="require" label="Platform" items={[platform]} />}
            {langs && <RuleRow variant="require" label="Language" items={langs} />}
            {tools && <RuleRow variant="require" label="Has tools" items={tools} />}
            {practices && <RuleRow variant="require" label="Has practice" items={practices} />}
          </div>
        ) : (
          <p className="text-xs text-gray-400">No requirements — any repository.</p>
        )}
      </div>

      {/* Excludes column */}
      <div className="rounded-lg bg-red-50/50 dark:bg-red-900/5 border border-red-100 dark:border-red-900/20 p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-red-500 dark:text-red-400 mb-3 flex items-center gap-1.5">
          <X className="w-3.5 h-3.5" /> Excluded When
        </h3>
        {hasExcludes ? (
          <div className="space-y-2">
            {Object.entries(exclude!).map(([key, values]) => (
              <RuleRow
                variant="exclude"
                key={key}
                label={EXCLUDE_LABELS[key] ?? key}
                items={values}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">No exclusions.</p>
        )}
      </div>
    </div>
  );
}

export const AdminMtDetailPage: React.FC = () => {
  const { taskClass } = useParams<{ taskClass: string }>();
  const [task, setTask] = useState<TaskDef | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/admin/task-catalog/${taskClass}`);
        if (res.data.success) setTask(res.data.data);
      } catch (err: unknown) {
        const e = err as { response?: { status?: number } };
        setError(e.response?.status === 404 ? 'Task not found.' : 'Failed to load task.');
        logger.error('Failed to fetch MT detail', { error: err });
      } finally {
        setLoading(false);
      }
    };
    if (taskClass) fetch();
  }, [taskClass]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Navbar />
        <main className="pt-24 px-4 pb-12 flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Navbar />
        <main className="pt-24 px-4 pb-12 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">{error ?? 'Not found'}</p>
            <Link
              to="/admin/maintenance-tasks"
              className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700"
            >
              Back to Maintenance Tasks
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const priorityCls =
    task.priority === 'high'
      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      : task.priority === 'medium'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navbar />
      <main className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto space-y-4">
          <Link
            to="/admin/maintenance-tasks"
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Maintenance Tasks
          </Link>

          {/* Header */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {task.displayName}
                </h1>
                <div className="flex items-center gap-3 mt-2">
                  <code className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                    {task.taskClass}
                  </code>
                  {task.category && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                      {task.category}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityCls}`}>
                    {task.priority}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Description
              </h2>
              <div className="prose prose-sm dark:prose-invert max-w-none prose-gray dark:prose-p:text-gray-400 dark:prose-li:text-gray-400 prose-headings:text-gray-800 dark:prose-headings:text-gray-200">
                <ReactMarkdown>{task.description}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Keywords */}
          {task.keywords.length > 0 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
              <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Keywords
              </h2>
              <div className="flex flex-wrap gap-2">
                {task.keywords.map((kw) => (
                  <span
                    key={kw}
                    className="px-2.5 py-1 rounded-full text-xs bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Applicability Rules */}
          {task.applicability && Object.keys(task.applicability).length > 0 && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Applicability Rules
              </h2>
              <ApplicabilityGrid rules={task.applicability as Record<string, unknown>} />
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};
